/**
 * Property URL Utilities
 *
 * Utilities for generating property details URLs based on property ID token.
 *
 * SECURITY: URL generation validates and normalizes property IDs before navigation.
 * ASSUMPTION: Backend canonical property IDs may include a `property#` prefix.
 * RISK: Malformed or empty IDs could break navigation; validation throws early.
 *
 * URL Formats:
 * - Canonical detail: /property/{property-id-token}
 * - Exit strategy analysis: /property/{property-id-token}/exit-strategy-analysis
 * - Property photo upload: /property/{property-id-token}/photos-upload
 * - Public photo gallery view: /property/photos/view?data=<unpadded-base64-json> (route + token built in code; persist token only as share_photos_data)
 * - Legacy list/detail index: /properties (see routes); `/properties/:id` is deprecated for detail links.
 * URL token is derived from `property.id` and strips any leading `property#`.
 */

/**
 * Strips a leading `property#` prefix (case-insensitive) and returns the URL token, or null if invalid.
 *
 * @param {string|null|undefined} rawPropertyId
 * @returns {string|null}
 */
export function normalizePropertyIdToken(rawPropertyId) {
  if (rawPropertyId == null) return null;
  const raw = String(rawPropertyId).trim();
  if (!raw) return null;
  const propertyIdToken = raw.replace(/^property#/i, "").trim();
  if (!propertyIdToken) return null;
  return propertyIdToken;
}

/**
 * Encodes customer + property id into an unpadded base64 JSON payload for photo gallery share links.
 *
 * @param {string|null|undefined} customer
 * @param {string|null|undefined} rawPropertyId
 * @returns {string|null}
 */
export function encodePropertyPhotosViewData(customer, rawPropertyId) {
  const cust = String(customer ?? "").trim();
  if (!cust) return null;
  const propertyIdToken = normalizePropertyIdToken(rawPropertyId);
  if (!propertyIdToken) return null;
  const payload = { customer: cust, property_id: propertyIdToken };
  return btoa(JSON.stringify(payload)).replace(/=+$/, "");
}

/**
 * Decodes a photo gallery share `data` query param into customer + property id.
 *
 * @param {string|null|undefined} dataParam
 * @returns {{ customer: string, propertyId: string }|null}
 */
export function decodePropertyPhotosViewData(dataParam) {
  if (dataParam == null) return null;
  const stripped = String(dataParam).replace(/\s/g, "");
  if (!stripped) return null;
  const remainder = stripped.length % 4;
  if (remainder === 1) return null;
  const withPadding = remainder === 0 ? stripped : stripped + "=".repeat(4 - remainder);
  try {
    const parsed = JSON.parse(atob(withPadding));
    if (!parsed || typeof parsed !== "object") return null;
    const customer = typeof parsed.customer === "string" ? parsed.customer.trim() : "";
    const propertyId = typeof parsed.property_id === "string" ? parsed.property_id.trim() : "";
    if (!customer || !propertyId) return null;
    return { customer, propertyId };
  } catch {
    return null;
  }
}

/**
 * Generates canonical property detail URL path using property id token.
 *
 * SECURITY: Validates property object and ID token before generating URL.
 *
 * @param {Object} property - Property object with id
 * @returns {string} URL path like "/property/abc-123"
 */
export const generatePropertyUrl = (property) => {
  if (!property || typeof property !== "object") {
    throw new Error("Property object is required");
  }

  const rawPropertyId = String(property.id || "").trim();
  if (!rawPropertyId) {
    throw new Error("Property ID is required for URL generation");
  }

  const propertyIdToken = rawPropertyId.replace(/^property#/, "").trim();
  if (!propertyIdToken) {
    throw new Error("Property ID token is invalid for URL generation");
  }

  return `/property/${propertyIdToken}`;
};

/**
 * Same as {@link generatePropertyUrl} — kept for call sites that migrated before naming settled.
 *
 * SECURITY: Validates property object and ID token before generating URL.
 *
 * @param {Object} property - Property object with id
 * @returns {string} URL path like "/property/abc-123"
 */
export const generateReplacementPropertyUrl = (property) => generatePropertyUrl(property);

/**
 * `/property/...` path for the property detail page from a raw id (e.g. GraphQL or process params).
 * Strips a leading `property#` prefix (case-insensitive) so the segment matches URL tokens.
 *
 * @param {string|null|undefined} rawPropertyId
 * @returns {string|null} Path like "/property/abc-123", or null if missing or empty after normalization
 */
export function propertyIdToDetailsPath(rawPropertyId) {
  if (rawPropertyId == null) return null;
  const raw = String(rawPropertyId).trim();
  if (!raw) return null;
  const propertyIdToken = raw.replace(/^property#/i, "").trim();
  if (!propertyIdToken) return null;
  return `/property/${encodeURIComponent(propertyIdToken)}`;
}

/**
 * Exit strategy analysis page path from a raw property id (same URL token rules as property detail).
 *
 * @param {string|null|undefined} rawPropertyId
 * @returns {string|null} Path like "/property/abc-123/exit-strategy-analysis", or null if missing or invalid
 */
export function propertyIdToExitStrategyAnalysisPath(rawPropertyId) {
  if (rawPropertyId == null) return null;
  const raw = String(rawPropertyId).trim();
  if (!raw) return null;
  const propertyIdToken = raw.replace(/^property#/i, "").trim();
  if (!propertyIdToken) return null;
  return `/property/${encodeURIComponent(propertyIdToken)}/exit-strategy-analysis`;
}

/**
 * Property photo upload page path from a raw property id (same URL token rules as property detail).
 *
 * @param {string|null|undefined} rawPropertyId
 * @returns {string|null} Path like "/property/abc-123/photos-upload", or null if missing or invalid
 */
export function propertyIdToPropertyPhotoUploadPath(rawPropertyId) {
  if (rawPropertyId == null) return null;
  const raw = String(rawPropertyId).trim();
  if (!raw) return null;
  const propertyIdToken = raw.replace(/^property#/i, "").trim();
  if (!propertyIdToken) return null;
  return `/property/${encodeURIComponent(propertyIdToken)}/photos-upload`;
}

/** @type {"/property/photos/view"} */
export const PROPERTY_PHOTOS_VIEW_ROUTE = "/property/photos/view";

/**
 * Public photo gallery view path from a persisted share token (unpadded base64 JSON).
 *
 * @param {string|null|undefined} dataToken
 * @returns {string|null} Path like "/property/photos/view?data=...", or null if invalid. Path-only (no origin).
 */
export function propertyPhotosViewPathFromData(dataToken) {
  const data = String(dataToken ?? "").trim();
  if (!data) return null;
  if (!decodePropertyPhotosViewData(data)) return null;
  const params = new URLSearchParams({ data });
  return `${PROPERTY_PHOTOS_VIEW_ROUTE}?${params.toString()}`;
}

/**
 * Public photo gallery view path from customer + raw property id.
 *
 * @param {string|null|undefined} customer
 * @param {string|null|undefined} rawPropertyId
 * @returns {string|null} Path like "/property/photos/view?data=...", or null if invalid. Path-only (no origin).
 */
export function propertyIdToPropertyPhotosViewPath(customer, rawPropertyId) {
  const data = encodePropertyPhotosViewData(customer, rawPropertyId);
  if (!data) return null;
  return propertyPhotosViewPathFromData(data);
}
